import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Snackbar,
  Alert,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import moment from 'moment';
import SearchField from '../components/SearchField';
import AppStyles from '../styles/AppStyles';
import { CardSkeleton } from '../components/SkeletonLoader';
import ErrorState from '../components/ErrorState';
import NoEntries from '../components/NoEntries';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000';

function Nutrition() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [error] = useState('');
  const [petId, setPetId] = useState(() => localStorage.getItem('currentPetId') || '');
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();

  const token = typeof window !== 'undefined' ? localStorage.getItem('vetToken') : null;
  const currentPetId = typeof window !== 'undefined' ? (localStorage.getItem('currentPetId') || petId) : petId;
  useEffect(() => setPetId(currentPetId), [currentPetId]);

  const sectionsQuery = useQuery({
    queryKey: ['sections', petId],
    enabled: !!petId,
    queryFn: async () => {
      const resp = await fetch(`${API_BASE}/api/sections?petId=${encodeURIComponent(petId)}&added=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await resp.json();
      return Array.isArray(data) ? data : [];
    },
    staleTime: 120000,
  });

  const nutritionSectionId = (sectionsQuery.data || []).find(s => {
    const name = String(s.name || '').trim().toLowerCase();
    return name === 'nutrition' || name.includes('diet') || name.includes('food');
  })?.id;

  const plansQuery = useQuery({
    queryKey: ['nutrition', petId, nutritionSectionId],
    enabled: !!petId && !!nutritionSectionId,
    queryFn: async () => {
      const resp = await fetch(`${API_BASE}/api/sections-nutrition?petId=${encodeURIComponent(petId)}&sectionId=${encodeURIComponent(nutritionSectionId)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await resp.json();
      return Array.isArray(data) ? data : [];
    },
    staleTime: 60000,
  });

  useEffect(() => {
    const handlePetChange = () => {
      const currentPetId = localStorage.getItem('currentPetId');
      if (currentPetId && currentPetId !== petId) {
        setPetId(currentPetId);
        queryClient.invalidateQueries({ queryKey: ['sections', currentPetId] });
        queryClient.invalidateQueries({ queryKey: ['nutrition', currentPetId] });
      }
    };
    window.addEventListener('storage', handlePetChange);
    window.addEventListener('petChanged', handlePetChange);
    return () => {
      window.removeEventListener('storage', handlePetChange);
      window.removeEventListener('petChanged', handlePetChange);
    };
  }, [petId, queryClient]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const matchesSearch = useCallback((plan) => {
    const term = (searchTerm || '').toLowerCase();
    if (!term) return true;
    return (
      (plan.foodName || '').toLowerCase().includes(term) ||
      (plan.brand || '').toLowerCase().includes(term) ||
      (plan.specialNotes || '').toLowerCase().includes(term)
    );
  }, [searchTerm]);
  const isActive = (p) => {
    const noStart = !p.startDate;
    const hasStartNoEnd = p.startDate && !p.endDate;
    const inFuture = p.startDate && p.endDate && new Date(p.endDate) > new Date();
    return noStart || hasStartNoEnd || inFuture;
  };
  const plans = useMemo(() => plansQuery.data || [], [plansQuery.data]);
  const currentPlans = useMemo(() => plans.filter(isActive).filter(matchesSearch), [plans, matchesSearch]);
  const allPlans = useMemo(() => plans.filter(matchesSearch), [plans, matchesSearch]);

  const handleCopyPlans = () => {
    const list = currentPlans || [];
    if (!Array.isArray(list) || list.length === 0) {
      navigator.clipboard.writeText('');
      return;
    }
    const lines = list.map((p) => {
      const brand = (p?.brand || '').trim();
      const showBrand = brand && brand.toLowerCase() !== 'other';
      const amount = (p?.quantity != null && p?.unit) ? `${p.quantity} ${p.unit}` : (p?.amount || '').trim();
      const hasAmount = !!amount;
      const freq = p?.frequencyNumber != null ? `${p.frequencyNumber}${p?.frequencyUnit ? ` ${p.frequencyUnit}` : ''}` : t('nutrition.asNeeded');
      const name = (p?.foodName || '-').trim();
      let text = '';
      if (showBrand) text += `${brand} - `;
      text += name;
      if (hasAmount) {
        text += ` ${amount}/${freq}`;
      }
      return text.trim();
    });
    navigator.clipboard.writeText(lines.join(', '));
    if (copied) {
      setCopied(false);
      setTimeout(() => setCopied(true), 0);
    } else {
      setCopied(true);
    }
  };

  // Early returns: loading, error, empty
  const loading = sectionsQuery.isLoading || plansQuery.isLoading;
  if (loading) {
    return <CardSkeleton count={3} />;
  }
  if (error || sectionsQuery.isError || plansQuery.isError) {
    return <ErrorState />;
  }
  if ((plans || []).length === 0) {
    return (
      <NoEntries
        title={t('common.noNutritionTitle')}
        description={t('common.noNutritionDesc')}
        icon="nutrition"
      />
    );
  }

  return (
    <Box>
      {!loading && !error && (
        <SearchField 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClear={() => setSearchTerm('')}
          placeholder={t('nutrition.searchPlaceholder')}
          ariaLabel={t('nutrition.searchAriaLabel')}
        />
      )}

      {/* Tabs */}
      {!loading && !error && (
      <Paper 
        sx={{ 
          mb: 3,
          p: 1,
          borderRadius: 3,
          background: 'transparent',
          border: `1px solid ${AppStyles.colors.border.light}`,
          boxShadow: AppStyles.shadows.sm,
        }}
      >
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          variant="fullWidth"
          sx={{
            minHeight: 48,
            '& .MuiTabs-flexContainer': { gap: 8 },
            '& .MuiTabs-indicator': { display: 'none' },
          }}
        >
          <Tab 
            label={t('nutrition.currentNutritionPlans')} 
            sx={{
              minHeight: 40,
              textTransform: 'none',
              borderRadius: 999,
              px: 2.5,
              color: AppStyles.colors.text.secondary,
              background: `linear-gradient(135deg, ${AppStyles.colors.primary.main}20 0%, ${AppStyles.colors.secondary.main}20 100%)`,
              fontWeight: AppStyles.typography.fontWeight.medium,
              '&:hover': {
                background: `linear-gradient(135deg, ${AppStyles.colors.primary.main}30 0%, ${AppStyles.colors.secondary.main}30 100%)`,
              },
              '&.Mui-selected': {
                color: AppStyles.colors.primary.contrastText,
                background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
                boxShadow: AppStyles.shadows.sm,
              },
            }}
          />
          <Tab 
            label={t('nutrition.feedingHistory')} 
            sx={{
              minHeight: 40,
              textTransform: 'none',
              borderRadius: 999,
              px: 2.5,
              color: AppStyles.colors.text.secondary,
              background: `linear-gradient(135deg, ${AppStyles.colors.primary.main}20 0%, ${AppStyles.colors.secondary.main}20 100%)`,
              fontWeight: AppStyles.typography.fontWeight.medium,
              '&:hover': {
                background: `linear-gradient(135deg, ${AppStyles.colors.primary.main}30 0%, ${AppStyles.colors.secondary.main}30 100%)`,
              },
              '&.Mui-selected': {
                color: AppStyles.colors.primary.contrastText,
                background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
                boxShadow: AppStyles.shadows.sm,
              },
            }}
          />
        </Tabs>
      </Paper>
      )}

      {/* Current Nutrition Plans Tab */}
      {loading && (
        <CardSkeleton count={3} />
      )}
      {error && <ErrorState />}

      {!loading && !error && activeTab === 0 && (
        <Paper 
          elevation={8}
          sx={{ 
            p: { xs: 2, md: 4 },
            borderRadius: 3,
            background: 'transparent',
            border: `1px solid ${AppStyles.colors.border.light}`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${AppStyles.colors.secondary.main} 0%, ${AppStyles.colors.primary.main} 100%)`,
            }
          }}
        >
          <TableContainer sx={{ width: '100%' }}>
            <Table sx={{ width: '100%' }}>
              <TableHead>
                <TableRow sx={{ '& th': { color: AppStyles.colors.primary.dark, fontWeight: AppStyles.typography.fontWeight.medium, fontSize: '0.8rem' } }}>
                  <TableCell>{t('nutrition.foodName')}</TableCell>
                  <TableCell>{t('nutrition.amount')}</TableCell>
                  <TableCell>{t('nutrition.frequency')}</TableCell>
                  <TableCell>{t('nutrition.brand')}</TableCell>
                  <TableCell>{t('nutrition.type')}</TableCell>
                  <TableCell>{t('nutrition.startDate')}</TableCell>
                  <TableCell>{t('nutrition.endDate')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentPlans.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                        {t('common.noCurrentNutritionTitle')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  currentPlans.map((plan, index) => (
                    <TableRow 
                      key={plan.id}
                      sx={{
                        '& td': {
                          borderBottom: `1px solid ${AppStyles.colors.border.light}`,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body1" sx={{ color: AppStyles.colors.text.primary, fontWeight: AppStyles.typography.fontWeight.medium, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {plan.foodName || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>
                          {(plan.quantity != null && plan.unit) ? `${plan.quantity} ${plan.unit}` : (plan.amount || '-')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>
                          {plan.frequencyNumber != null ? `${plan.frequencyNumber} ${plan.frequencyUnit || ''}` : t('nutrition.asNeeded')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>{plan.brand || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>{plan.foodType || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>{plan.startDate ? moment(plan.startDate).format('MMM DD, YYYY') : '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>{plan.endDate ? moment(plan.endDate).format('MMM DD, YYYY') : '-'}</Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider sx={{ mt: 1, borderColor: AppStyles.colors.border.light, opacity: 0.6 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyPlans}
              sx={{
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: AppStyles.typography.fontWeight.medium,
                backgroundColor: 'rgba(188, 185, 118, 0.12)',
                color: AppStyles.colors.primary.dark,
                borderColor: 'rgba(188, 185, 118, 0.35)',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(188, 185, 118, 0.18)',
                  borderColor: 'rgba(188, 185, 118, 0.45)',
                  boxShadow: 'none',
                }
              }}
            >
              {t('nutrition.copy')}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Feeding History Tab */}
      {!loading && !error && activeTab === 1 && (
        <Paper 
          elevation={8}
          sx={{ 
            p: { xs: 2, md: 4 },
            borderRadius: 3,
            background: 'transparent',
            border: `1px solid ${AppStyles.colors.border.light}`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${AppStyles.colors.secondary.main} 0%, ${AppStyles.colors.primary.main} 100%)`,
            }
          }}
        >
          <TableContainer sx={{ width: '100%' }}>
            <Table sx={{ width: '100%' }}>
              <TableHead>
                <TableRow sx={{ '& th': { color: AppStyles.colors.primary.dark, fontWeight: AppStyles.typography.fontWeight.medium, fontSize: '0.8rem' } }}>
                  <TableCell>{t('nutrition.foodName')}</TableCell>
                  <TableCell>{t('nutrition.amount')}</TableCell>
                  <TableCell>{t('nutrition.frequency')}</TableCell>
                  <TableCell>{t('nutrition.brand')}</TableCell>
                  <TableCell>{t('nutrition.type')}</TableCell>
                  <TableCell>{t('nutrition.startDate')}</TableCell>
                  <TableCell>{t('nutrition.endDate')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allPlans.map((plan, index) => (
                  <TableRow 
                    key={plan.id || index}
                    sx={{
                      '& td': {
                        borderBottom: `1px solid ${AppStyles.colors.border.light}`,
                      },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body1" sx={{ color: AppStyles.colors.text.primary, fontWeight: AppStyles.typography.fontWeight.medium, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {plan.foodName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>
                        {(plan.quantity != null && plan.unit) ? `${plan.quantity} ${plan.unit}` : (plan.amount || '-')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>
                        {plan.frequencyNumber != null ? `${plan.frequencyNumber} ${plan.frequencyUnit || ''}` : t('nutrition.asNeeded')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>{plan.brand || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>{plan.foodType || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>{plan.startDate ? moment(plan.startDate).format('MMM DD, YYYY') : '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>{plan.endDate ? moment(plan.endDate).format('MMM DD, YYYY') : '-'}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* No empty-state message; keep the card clean and consistent */}
      <Snackbar
        open={copied}
        onClose={() => setCopied(false)}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setCopied(false)} 
          severity="success" 
          variant="filled"
          sx={{
            background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
            color: AppStyles.colors.primary.contrastText,
            boxShadow: AppStyles.shadows.sm,
            fontWeight: AppStyles.typography.fontWeight.medium,
          }}
        >
          {t('nutrition.nutritionPlansCopied')}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Nutrition;
